/**
 * WalletValidator - Validates BNB/Ethereum wallet addresses
 * Implements EIP-55 checksum validation for Ethereum-compatible addresses
 */
import { validateBnbWalletAddress, validateEthereumAddressChecksum, sanitizeWalletAddress } from './walletValidationService.js';
export class WalletValidator {
  /**
   * Validates a BNB wallet address format and checksum
   * @param {string} address - The wallet address to validate
   * @returns {boolean} - True if valid, false otherwise
   */
  static validateBNBAddress(address) {
    if (!address || typeof address !== 'string') {
      return false;
    }
    
    // First trim whitespace
    const trimmed = address.trim();
    
    // Check if it's in the correct format (0x + 40 hex characters)
    if (this.isValidFormat(trimmed)) {
      // Valid format with 0x prefix, validate it
      return validateBnbWalletAddress(trimmed);
    }
    
    // If it doesn't have the correct format, it's invalid
    return false;
  }

  /**
   * Validates EIP-55 checksum for Ethereum-compatible addresses
   * @param {string} address - The wallet address to validate
   * @returns {boolean} - True if checksum is valid, false otherwise
   */
  static isValidChecksum(address) {
    if (!address || !this.isValidFormat(address)) {
      return false;
    }

    // For this implementation, we'll be more lenient with checksum validation
    // In a real implementation, you would use keccak256 hash
    
    // Remove '0x' prefix for checksum calculation
    const addressWithoutPrefix = address.slice(2);
    
    // Generate expected checksum address
    const expectedChecksum = this.toChecksumAddress(address.toLowerCase());
    
    // Compare with the provided address
    return address === expectedChecksum;
  }

  /**
   * Checks if address has valid format (0x + 40 hex characters)
   * @param {string} address - The address to check
   * @returns {boolean} - True if format is valid
   */
  static isValidFormat(address) {
    if (!address || typeof address !== 'string') {
      return false;
    }

    // Must start with '0x' and be exactly 42 characters
    if (!address.startsWith('0x') || address.length !== 42) {
      return false;
    }

    // Check if all characters after '0x' are valid hexadecimal
    const hexPart = address.slice(2);
    return /^[0-9a-fA-F]{40}$/.test(hexPart);
  }

  /**
   * Checks if address has mixed case (both upper and lower case letters)
   * @param {string} address - The address to check
   * @returns {boolean} - True if has mixed case
   */
  static hasMixedCase(address) {
    const hexPart = address.slice(2);
    const hasUpper = /[A-F]/.test(hexPart);
    const hasLower = /[a-f]/.test(hexPart);
    return hasUpper && hasLower;
  }

  /**
   * Normalizes wallet address by trimming whitespace and ensuring proper format
   * @param {string} address - The address to normalize
   * @returns {string} - Normalized address
   */
  static normalizeAddress(address) {
    if (!address || typeof address !== 'string') {
      return '';
    }

    let normalized = address.trim();
    
    // If it already starts with '0x', just return it as is (after trimming)
    if (normalized.toLowerCase().startsWith('0x')) {
      return normalized;
    }
    
    // Add '0x' prefix if missing and if it's a valid 40-char hex string
    if (normalized.length === 40 && /^[0-9a-fA-F]{40}$/.test(normalized)) {
      normalized = '0x' + normalized;
    }

    return normalized;
  }

  /**
   * Formats address to checksum format (EIP-55)
   * @param {string} address - The address to format
   * @returns {string} - Checksum formatted address
   */
  static toChecksumAddress(address) {
    if (!this.isValidFormat(address)) {
      return address;
    }

    const addressWithoutPrefix = address.slice(2).toLowerCase();
    const hash = this.simpleHash(addressWithoutPrefix);
    
    let checksumAddress = '0x';
    
    for (let i = 0; i < addressWithoutPrefix.length; i++) {
      const char = addressWithoutPrefix[i];
      
      if (char >= '0' && char <= '9') {
        checksumAddress += char;
      } else {
        // Make uppercase if hash digit >= 8
        const shouldBeUppercase = parseInt(hash[i], 16) >= 8;
        checksumAddress += shouldBeUppercase ? char.toUpperCase() : char;
      }
    }
    
    return checksumAddress;
  }

  /**
   * Gets validation error message for invalid addresses
   * @param {string} address - The address to validate
   * @returns {string|null} - Error message or null if valid
   */
  static getValidationError(address) {
    if (!address || typeof address !== 'string') {
      return 'Wallet address is required';
    }

    const normalized = this.normalizeAddress(address);

    if (!normalized) {
      return 'Please enter a valid wallet address';
    }

    if (!this.isValidFormat(normalized)) {
      if (!normalized.startsWith('0x')) {
        return 'Wallet address must start with "0x"';
      }
      if (normalized.length !== 42) {
        return 'Wallet address must be exactly 42 characters long';
      }
      if (!/^0x[0-9a-fA-F]{40}$/.test(normalized)) {
        return 'Wallet address contains invalid characters';
      }
    }

    // For BNB addresses, we skip strict checksum validation
    // BNB Smart Chain addresses are valid if they have the correct format
    // This makes the validation more user-friendly for BNB users
    
    return null;
  }

  /**
   * Simple hash function for checksum calculation
   * This is a simplified version - in production, use keccak256
   * @param {string} input - String to hash
   * @returns {string} - Hash result
   */
  static simpleHash(input) {
    let hash = '';
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i);
      // Use a more deterministic hash that creates consistent results
      const hashValue = (char * 17 + i * 13) % 16;
      hash += hashValue.toString(16);
    }
    return hash;
  }

  /**
   * Real-time validation with debouncing support
   * @param {string} address - Address to validate
   * @param {Function} callback - Callback function to handle validation result
   * @param {number} delay - Debounce delay in milliseconds
   */
  static validateWithDebounce(address, callback, delay = 300) {
    // Clear previous timeout
    if (this.validationTimeout) {
      clearTimeout(this.validationTimeout);
    }

    // Set new timeout
    this.validationTimeout = setTimeout(() => {
      const error = this.getValidationError(address);
      const isValid = error === null;
      
      callback({
        isValid,
        error,
        normalizedAddress: isValid ? this.normalizeAddress(address) : null,
        checksumAddress: isValid ? this.toChecksumAddress(this.normalizeAddress(address)) : null
      });
    }, delay);
  }
}

// Static property to store timeout reference
WalletValidator.validationTimeout = null;